import { useState, useRef, useCallback } from 'react';
import {
  uploadInvoiceAttachment,
  deleteInvoiceAttachment,
  reorderInvoiceAttachments,
  updateInvoiceAttachment,
  type InvoiceAttachment,
} from '@lib/api/invoices';

interface InvoiceAttachmentsSectionProps {
  invoiceId: number;
  jobId?: number;
  attachments: InvoiceAttachment[];
  onAttachmentsChange: () => void;
  onPickFromDocuments?: () => void;
  readOnly?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageType(type: string): boolean {
  return type.startsWith('image/');
}

function getFileIcon(type: string): string {
  if (type === 'application/pdf') return 'picture_as_pdf';
  if (type.startsWith('image/')) return 'image';
  return 'description';
}

export const InvoiceAttachmentsSection: React.FC<InvoiceAttachmentsSectionProps> = ({
  invoiceId,
  jobId,
  attachments,
  onAttachmentsChange,
  onPickFromDocuments,
  readOnly = false,
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState<number | null>(null);
  const [labelValue, setLabelValue] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [invoiceId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (files: FileList) => {
    setUploading(true);
    setError(null);
    try {
      for (let i = 0; i < files.length; i++) {
        await uploadInvoiceAttachment(invoiceId, files[i]);
      }
      onAttachmentsChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (attachmentId: number) => {
    if (!confirm('Remove this attachment?')) return;
    setError(null);
    try {
      await deleteInvoiceAttachment(invoiceId, attachmentId);
      onAttachmentsChange();
    } catch {
      setError('Failed to delete attachment');
    }
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= attachments.length) return;

    const newOrder = attachments.map((a, i) => {
      if (i === index) return { id: a.id, display_order: swapIndex };
      if (i === swapIndex) return { id: a.id, display_order: index };
      return { id: a.id, display_order: i };
    });

    try {
      await reorderInvoiceAttachments(invoiceId, newOrder);
      onAttachmentsChange();
    } catch {
      setError('Failed to reorder attachments');
    }
  };

  const handleLabelEdit = (attachment: InvoiceAttachment) => {
    setEditingLabel(attachment.id);
    setLabelValue(attachment.label || '');
  };

  const handleLabelSave = async (attachmentId: number) => {
    try {
      await updateInvoiceAttachment(invoiceId, attachmentId, { label: labelValue || undefined });
      setEditingLabel(null);
      onAttachmentsChange();
    } catch {
      setError('Failed to update label');
    }
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-white mb-3">Attachments</h3>

      {error && (
        <div className="mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-2">
          <span className="material-symbols-outlined text-lg">error</span>
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>
      )}

      {/* Upload zone */}
      {!readOnly && (
        <div className="flex gap-3 mb-3">
          <label
            className={`flex flex-col items-center justify-center flex-1 h-28 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
              dragActive
                ? 'border-blue-500 bg-blue-500/10'
                : 'border-gray-600 bg-[#101922] hover:bg-gray-800'
            } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center justify-center">
              {uploading ? (
                <>
                  <span className="material-symbols-outlined animate-spin text-3xl text-blue-400">progress_activity</span>
                  <p className="mt-1 text-sm text-gray-400">Uploading...</p>
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-3xl text-gray-500">cloud_upload</span>
                  <p className="mt-1 text-sm text-gray-400">
                    <span className="font-semibold">Click to upload</span> or drag & drop
                  </p>
                  <p className="text-xs text-gray-500">Images, PDFs, Documents (max 10MB)</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
              onChange={handleChange}
            />
          </label>

          {jobId && onPickFromDocuments && (
            <button
              onClick={onPickFromDocuments}
              className="flex flex-col items-center justify-center w-36 h-28 border-2 border-dashed border-gray-600 rounded-lg hover:bg-gray-800 transition-colors"
            >
              <span className="material-symbols-outlined text-3xl text-gray-500">folder_open</span>
              <p className="mt-1 text-xs font-semibold text-gray-400 text-center">Add from Job<br />Documents</p>
            </button>
          )}
        </div>
      )}

      {/* Attachments list */}
      {attachments.length > 0 ? (
        <div className="space-y-2">
          {attachments.map((att, index) => (
            <div
              key={att.id}
              className="flex items-center gap-3 bg-[#101922] p-3 rounded-lg border border-gray-700"
            >
              {/* Thumbnail / icon */}
              <div className="w-10 h-10 rounded bg-gray-800 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {isImageType(att.file_type) ? (
                  <img src={att.url} alt={att.file_name} className="w-full h-full object-cover" />
                ) : (
                  <span className="material-symbols-outlined text-gray-400">{getFileIcon(att.file_type)}</span>
                )}
              </div>

              {/* File info */}
              <div className="flex-1 min-w-0">
                {editingLabel === att.id ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={labelValue}
                      onChange={(e) => setLabelValue(e.target.value)}
                      placeholder="Label (optional)"
                      className="flex-1 bg-gray-800 text-white text-sm px-2 py-1 rounded border border-gray-600 focus:border-blue-500 outline-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleLabelSave(att.id);
                        if (e.key === 'Escape') setEditingLabel(null);
                      }}
                      autoFocus
                    />
                    <button onClick={() => handleLabelSave(att.id)} className="text-green-400 hover:text-green-300">
                      <span className="material-symbols-outlined text-lg">check</span>
                    </button>
                    <button onClick={() => setEditingLabel(null)} className="text-gray-400 hover:text-gray-300">
                      <span className="material-symbols-outlined text-lg">close</span>
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="text-white text-sm truncate">
                      {att.label || att.file_name}
                      {att.document_id && (
                        <span className="ml-2 text-xs text-blue-400" title="Linked from job documents">linked</span>
                      )}
                    </div>
                    <div className="text-gray-500 text-xs">
                      {att.file_name !== (att.label || att.file_name) && `${att.file_name} - `}
                      {formatFileSize(att.file_size)}
                    </div>
                  </>
                )}
              </div>

              {/* Actions */}
              {!readOnly && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleMove(index, 'up')}
                    disabled={index === 0}
                    className="p-1 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    <span className="material-symbols-outlined text-lg">arrow_upward</span>
                  </button>
                  <button
                    onClick={() => handleMove(index, 'down')}
                    disabled={index === attachments.length - 1}
                    className="p-1 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    <span className="material-symbols-outlined text-lg">arrow_downward</span>
                  </button>
                  <button
                    onClick={() => handleLabelEdit(att)}
                    className="p-1 text-gray-400 hover:text-blue-400"
                    title="Edit label"
                  >
                    <span className="material-symbols-outlined text-lg">edit</span>
                  </button>
                  <button
                    onClick={() => handleDelete(att.id)}
                    className="p-1 text-gray-400 hover:text-red-400"
                    title="Remove"
                  >
                    <span className="material-symbols-outlined text-lg">delete</span>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-gray-500 text-sm">
          <span className="material-symbols-outlined text-3xl mb-1 opacity-50 block">attach_file</span>
          No attachments
        </div>
      )}
    </div>
  );
};
